import type { MailboxDto } from '@glory/contracts';
import { t } from '@glory/i18n';
import {
  Avatar,
  Button,
  Callout,
  Card,
  ContentSwap,
  Dialog,
  EmptyState,
  notify,
  PageHeader,
  SettingsSection,
  Skeleton,
  SkeletonRows,
} from '@glory/ui';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Copy, MailPlus, Mails, RotateCcw, Users } from 'lucide-react';
import { useState } from 'react';

import { AccessDialog } from '../../../features/mail/access-dialog.tsx';
import {
  CreatePersonalDialog,
  type MailPersonView,
  NewSharedDialog,
  RetryDialog,
} from '../../../features/mail/mailbox-dialogs.tsx';
import { MailboxStatusBadge } from '../../../features/mail/status-badge.tsx';
import { MailTo } from '../../../features/webmail/mail-to.tsx';
import { type client, orpc } from '../../../lib/orpc.ts';
import { PersonAvatar } from '../../../lib/people.tsx';
import { requireSettingsPermission } from '../../../lib/permissions.ts';

export const Route = createFileRoute('/_app/settings/mail')({
  beforeLoad: requireSettingsPermission('mail.manage'),
  component: MailSettingsPage,
});

type Settings = Awaited<ReturnType<typeof client.mail.settings>>;

type OpenDialog =
  | { readonly kind: 'shared' }
  | { readonly kind: 'retry'; readonly id: string }
  | { readonly kind: 'access'; readonly id: string }
  | { readonly kind: 'personal'; readonly person: MailPersonView };

const FAILURE_TEXT = {
  address_taken: t('mail.failureAddressTaken'),
  rejected: t('mail.failureRejected'),
  unavailable: t('mail.failureUnavailable'),
} as const;

/** Сколько доступов показать в строке общего ящика; остальные — числом. */
const ACCESS_PREVIEW = 3;

function MailSettingsPage() {
  const settings = useQuery(orpc.mail.settings.queryOptions());
  // Загрузка, сбой, «почта не настроена» и настройки проявляются друг из друга, без скачка высоты.
  const phase = settings.isPending
    ? 'loading'
    : settings.isError
      ? 'error'
      : settings.data.configured && settings.data.domain
        ? 'ready'
        : 'off';
  return (
    <div className="flex flex-col gap-4 px-4 py-5 md:px-6">
      <PageHeader title={t('mail.title')} />
      <p className="-mt-2 text-muted">{t('mail.lead')}</p>
      <ContentSwap swapKey={phase} contentClassName="flex flex-col gap-4">
        {settings.isPending ? (
          <>
            <Skeleton className="h-16" />
            <SkeletonRows rows={4} />
          </>
        ) : settings.isError ? (
          <Callout>{settings.error.message}</Callout>
        ) : settings.data.configured && settings.data.domain ? (
          <MailSettings settings={settings.data} domain={settings.data.domain} />
        ) : (
          <Card>
            <EmptyState
              icon={<Mails />}
              title={t('mail.notConfiguredTitle')}
              description={t('mail.notConfiguredText')}
            />
          </Card>
        )}
      </ContentSwap>
    </div>
  );
}

function MailSettings({ settings, domain }: { settings: Settings; domain: string }) {
  const [dialog, setDialog] = useState<OpenDialog | null>(null);
  const list = useQuery(
    orpc.mail.mailboxes.list.queryOptions({
      // Ящик создаёт worker: пока какой-то создаётся, перечитываем список.
      refetchInterval: (query) =>
        query.state.data?.mailboxes.some((m) => m.status === 'provisioning') ? 2_000 : false,
    }),
  );
  const close = () => {
    setDialog(null);
  };

  const mailboxes = list.data?.mailboxes ?? [];
  const people = list.data?.people ?? [];
  const failed = mailboxes.filter((m) => m.status === 'failed');
  const shared = mailboxes.filter((m) => m.kind === 'shared' && m.status !== 'failed');
  const personal = mailboxes.filter((m) => m.kind === 'personal' && m.status !== 'failed');
  const withoutMailbox = people.filter((p) => p.active && p.mailboxId === null);
  const inactive = new Set(people.filter((p) => !p.active).map((p) => p.id));
  const opened =
    dialog && dialog.kind !== 'shared' && dialog.kind !== 'personal'
      ? mailboxes.find((m) => m.id === dialog.id)
      : null;

  return (
    <>
      <ServerStrip settings={settings} domain={domain} />
      {settings.reachable ? null : <Callout>{t('mail.serverDownLead')}</Callout>}

      <ContentSwap
        swapKey={list.isPending ? 'loading' : list.isError ? 'error' : 'ready'}
        contentClassName="flex flex-col gap-4"
      >
        {list.isPending ? (
          <SkeletonRows rows={5} />
        ) : list.isError ? (
          <Callout>{list.error.message}</Callout>
        ) : (
          <>
            {failed.length > 0 ? (
              <SettingsSection
                className="border-danger"
                title={t('mail.failedTitle')}
                lead={t('mail.failedLead')}
              >
                <ul className="flex flex-col divide-y divide-border-soft">
                  {failed.map((mailbox) => (
                    <li
                      key={mailbox.id}
                      className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2.5 first:pt-0 last:pb-0"
                    >
                      <div className="flex min-w-0 flex-1 flex-col">
                        <span className="truncate font-semibold">{mailbox.address}</span>
                        <span className="text-caption text-danger">
                          {mailbox.failure ? FAILURE_TEXT[mailbox.failure] : mailbox.lastError}
                        </span>
                        <span className="truncate text-caption text-muted">{whose(mailbox)}</span>
                      </div>
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => {
                          setDialog({ kind: 'retry', id: mailbox.id });
                        }}
                      >
                        <RotateCcw /> {t('mail.retry')}
                      </Button>
                    </li>
                  ))}
                </ul>
              </SettingsSection>
            ) : null}

            <SettingsSection
              title={t('mail.sharedTitle')}
              lead={t('mail.sharedLead')}
              action={
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    setDialog({ kind: 'shared' });
                  }}
                >
                  <MailPlus /> {t('mail.newShared')}
                </Button>
              }
            >
              {shared.length === 0 ? (
                <p className="text-small text-muted">{t('mail.sharedEmpty')}</p>
              ) : (
                <ul className="flex flex-col divide-y divide-border-soft">
                  {shared.map((mailbox) => (
                    <li
                      key={mailbox.id}
                      className="grid grid-cols-[minmax(0,1fr)_auto_auto] items-center gap-x-3 gap-y-1 py-2.5 first:pt-0 last:pb-0"
                    >
                      <div className="flex min-w-0 flex-col">
                        <span className="flex items-center gap-1">
                          <span className="truncate font-semibold">{mailbox.address}</span>
                          {mailbox.status === 'active' ? <MailTo email={mailbox.address} /> : null}
                        </span>
                        <AccessSummary mailbox={mailbox} />
                      </div>
                      <MailboxStatusBadge status={mailbox.status} />
                      <Button
                        size="sm"
                        onClick={() => {
                          setDialog({ kind: 'access', id: mailbox.id });
                        }}
                      >
                        <Users /> {t('mail.access')}
                      </Button>
                    </li>
                  ))}
                </ul>
              )}
            </SettingsSection>

            <SettingsSection title={t('mail.personalTitle')} lead={t('mail.personalLead')}>
              {personal.length === 0 ? (
                <p className="text-small text-muted">{t('mail.personalEmpty')}</p>
              ) : (
                <ul className="flex flex-col divide-y divide-border-soft">
                  {personal.map((mailbox) => (
                    <li
                      key={mailbox.id}
                      className="grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-x-3 py-2.5 first:pt-0 last:pb-0"
                    >
                      {mailbox.owner ? (
                        <PersonAvatar person={mailbox.owner} size="md" />
                      ) : (
                        <Avatar name={whose(mailbox)} size="md" />
                      )}
                      <div className="flex min-w-0 flex-col">
                        <span className="truncate font-semibold">{mailbox.address}</span>
                        <span className="truncate text-caption text-muted">
                          {whose(mailbox)}
                          {mailbox.owner && inactive.has(mailbox.owner.id)
                            ? `, ${t('mail.inactivePerson')}`
                            : ''}
                        </span>
                      </div>
                      <MailboxStatusBadge status={mailbox.status} />
                    </li>
                  ))}
                </ul>
              )}
              {withoutMailbox.length > 0 ? (
                <div className="mt-4 flex flex-col gap-1 border-t border-border-soft pt-3">
                  <h3 className="font-bold">{t('mail.withoutMailbox')}</h3>
                  <p className="text-caption text-muted">{t('mail.withoutMailboxLead')}</p>
                  <ul className="mt-1 flex flex-col divide-y divide-border-soft">
                    {withoutMailbox.map((person) => (
                      <li key={person.id} className="flex items-center gap-3 py-2 last:pb-0">
                        <PersonAvatar person={person} size="md" />
                        <div className="flex min-w-0 flex-1 flex-col">
                          <span className="truncate font-semibold">{person.name}</span>
                          <span className="truncate text-caption text-muted">{person.email}</span>
                        </div>
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => {
                            setDialog({ kind: 'personal', person });
                          }}
                        >
                          {t('mail.createMailbox')}
                        </Button>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </SettingsSection>
          </>
        )}
      </ContentSwap>

      <DnsSection records={settings.dnsRecords} reachable={settings.reachable} />
      {settings.clients ? <ClientsSection clients={settings.clients} /> : null}

      <Dialog
        open={dialog !== null}
        onOpenChange={(open) => {
          if (!open) close();
        }}
      >
        {dialog?.kind === 'shared' ? <NewSharedDialog domain={domain} onDone={close} /> : null}
        {dialog?.kind === 'personal' ? (
          <CreatePersonalDialog person={dialog.person} domain={domain} onDone={close} />
        ) : null}
        {dialog?.kind === 'retry' && opened ? (
          <RetryDialog mailbox={opened} domain={domain} onDone={close} />
        ) : null}
        {dialog?.kind === 'access' && opened ? <AccessDialog mailbox={opened} people={people} /> : null}
      </Dialog>
    </>
  );
}

/** Чей ящик: владелец, приглашённый по адресу или назначение общего. */
function whose(mailbox: MailboxDto): string {
  if (mailbox.kind === 'shared') return mailbox.description ?? '';
  if (mailbox.owner?.name) return mailbox.owner.name;
  return mailbox.personEmail
    ? t('mail.invited', { email: mailbox.personEmail })
    : (mailbox.description ?? '');
}

function AccessSummary({ mailbox }: { mailbox: MailboxDto }) {
  const description = mailbox.description ? <span>{mailbox.description}. </span> : null;
  if (mailbox.access.length === 0) {
    return (
      <span className="truncate text-caption text-muted">
        {description}
        {t('mail.accessNobody')}
      </span>
    );
  }
  const shown = mailbox.access.slice(0, ACCESS_PREVIEW);
  const rest = mailbox.access.length - shown.length;
  return (
    <span className="truncate text-caption text-muted">
      {description}
      {shown.map((grant, index) => (
        <span key={grant.userId}>
          {index > 0 ? ', ' : null}
          <span className="font-semibold text-text">{grant.name}</span>{' '}
          {grant.canSend ? t('mail.levelSendShort') : t('mail.levelReadShort')}
        </span>
      ))}
      {rest > 0 ? ` +${String(rest)}` : null}
    </span>
  );
}

/** Домен студии, имя сервера и отвечает ли он — первое, что нужно знать о почте. */
function ServerStrip({ settings, domain }: { settings: Settings; domain: string }) {
  return (
    <Card className="grid divide-y divide-border-soft sm:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)_auto] sm:divide-x sm:divide-y-0">
      <div className="flex min-w-0 flex-col gap-0.5 px-4 py-3">
        <span className="text-caption font-semibold text-muted">{t('mail.domain')}</span>
        <span className="truncate font-display text-title font-bold">{domain}</span>
      </div>
      <div className="flex min-w-0 flex-col gap-0.5 px-4 py-3">
        <span className="text-caption font-semibold text-muted">{t('mail.server')}</span>
        <span className="truncate font-semibold">{settings.hostname}</span>
      </div>
      <div className="flex items-center gap-2 px-4 py-3 font-bold">
        <span
          aria-hidden="true"
          className={
            settings.reachable
              ? 'size-2.5 rounded-full bg-strong ring-2 ring-ink'
              : 'size-2.5 rounded-full bg-danger'
          }
        />
        <span className={settings.reachable ? undefined : 'text-danger'}>
          {settings.reachable ? t('mail.serverOk') : t('mail.serverDown')}
        </span>
      </div>
    </Card>
  );
}

function DnsSection({ records, reachable }: { records: string | null; reachable: boolean }) {
  return (
    <SettingsSection
      title={t('mail.dnsTitle')}
      lead={t('mail.dnsLead')}
      action={
        records ? (
          <Button
            size="sm"
            onClick={() => {
              void navigator.clipboard.writeText(records).then(() => {
                notify(t('mail.dnsCopied'));
              });
            }}
          >
            <Copy /> {t('mail.copyDns')}
          </Button>
        ) : null
      }
    >
      {records ? (
        <pre className="max-h-72 overflow-auto rounded-md bg-surface-2 p-3 font-mono text-caption leading-relaxed whitespace-pre">
          {records}
        </pre>
      ) : (
        <p className="text-small text-muted">{reachable ? t('mail.dnsEmpty') : t('mail.serverDownLead')}</p>
      )}
    </SettingsSection>
  );
}

function ClientsSection({ clients }: { clients: NonNullable<Settings['clients']> }) {
  const rows = [
    [t('mail.imap'), `${clients.host}, ${t('mail.port', { port: String(clients.imapPort) })}`],
    [t('mail.smtp'), `${clients.host}, ${t('mail.port', { port: String(clients.smtpPort) })}`],
    [t('mail.encryption'), t('mail.encryptionValue')],
    [t('mail.login'), t('mail.loginValue')],
  ] as const;
  return (
    <SettingsSection title={t('mail.clientsTitle')} lead={t('mail.clientsLead')}>
      <dl className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-1.5 text-small">
        {rows.map(([label, value]) => (
          <div key={label} className="contents">
            <dt className="text-muted">{label}</dt>
            <dd className="font-semibold">{value}</dd>
          </div>
        ))}
      </dl>
    </SettingsSection>
  );
}
